"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, Suspense } from "react";
import * as z from "zod";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";

import { LoginSchema } from "@/schemas";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { FormError } from "@/components/form-error";
import { SocialLogins } from "@/components/auth/social-logins";

function LoginFormContent({ callbackUrl }: { callbackUrl: string }) {
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<z.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof LoginSchema>) => {
    setIsLoading(true);
    setError(undefined);

    const response = await signIn("credentials", {
      redirect: false,
      callbackUrl,
      email: values.email.toLowerCase(),
      password: values.password,
    });

    setIsLoading(false);

    if (response?.error) {
      setError("Invalid email or password.");
      return;
    }

    if (response?.ok && response.url) {
      window.location.href = response.url;
    }
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm" style={{ color: 'var(--fleet-muted)' }}>Email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="you@example.com"
                      type="email"
                      disabled={isLoading}
                      onChange={(e) => field.onChange(e.target.value.toLowerCase())}
                      className="text-sm transition-all duration-200 focus:scale-[1.02] hover:border-opacity-60 border-primary"
                      style={{ background: 'var(--fleet-subtle)', color: 'var(--fleet-text)' }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center justify-between text-sm" style={{ color: 'var(--fleet-muted)' }}>
                    Password
                    <Link
                      href="/auth/reset"
                      className="text-[12px] underline transition-all duration-200 hover:opacity-80 hover:scale-105"
                      style={{ color: 'var(--fleet-accent)' }}
                    >
                      Forgot?
                    </Link>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="password"
                      placeholder="********"
                      disabled={isLoading}
                      className="text-sm transition-all duration-200 focus:scale-[1.02] hover:border-opacity-60 border-primary"
                      style={{ background: 'var(--fleet-subtle)', color: 'var(--fleet-text)' }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormError message={error} />

          <Button
            type="submit"
            className="w-full text-[14px] transition-all duration-200 hover:scale-105 hover:shadow-lg disabled:hover:scale-100 disabled:hover:shadow-none bg-primary text-primary-foreground hover:bg-white hover:text-primary border border-primary"
            disabled={isLoading}
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
      </Form>

      <div className="mt-6 text-center text-sm" style={{ color: 'var(--fleet-muted)' }}>
        New to Smart Taurus?{" "}
        <Link
          href={`/auth/register?callbackUrl=${encodeURIComponent(callbackUrl)}&from=login`}
          className="underline font-medium transition-all duration-200 hover:opacity-80 hover:scale-105"
          style={{ color: 'var(--fleet-accent)' }}
        >
          Create account
        </Link>
      </div>

      <Suspense fallback={<div className="mt-6">Loading social logins...</div>}>
        <SocialLogins callbackUrl={callbackUrl} fleetMode={true} />
      </Suspense>
    </>
  );
}

export const LoginForm = () => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LoginFormWithParams />
    </Suspense>
  );
};

function LoginFormWithParams() {
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") || "/dashboard";
  
  return <LoginFormContent callbackUrl={callbackUrl} />;
}
